import { Route, Routes } from '@angular/router';
import { adminGuard } from './guards/admin-guard';
import { AuthGuard } from './guards/auth-guard';
import { authRedirectGuard } from './guards/auth-redirect-guard';
import { practiceAdminGuard } from './guards/practice-admin-guard';
import { practiceEmployeeGuard } from './guards/practice-employee-guard';
import { ShellLayoutComponent } from './layouts/shell-layout';
import { Signin } from './routes/signin/signin';
import { Signup } from './routes/signup/signup';
import { TokenComponent } from './routes/token/token';

export const MAIN_APP_ROUTES: Route[] = [
  {
    path: 'onboarding',
    loadComponent: () =>
      import('./routes/onboarding/onboarding').then((m) => m.Onboarding),
  },
  {
    path: '',
    loadComponent: () => import('./routes/home/home').then((m) => m.Home),
  },
  {
    path: 'sentence/:id',
    loadComponent: () =>
      import('./routes/exercises/sentence/sentence').then(
        (m) => m.SentenceComponent,
      ),
  },
  {
    path: 'word-level/:id',
    loadComponent: () =>
      import('./routes/exercises/word-level/word-level').then(
        (m) => m.WordLevel,
      ),
  },
  {
    path: 'memory-level/:id',
    loadComponent: () =>
      import('./routes/exercises/memory-level/memory-level').then(
        (m) => m.MemoryLevel,
      ),
  },
  {
    path: 'audio-match/:id',
    loadComponent: () =>
      import('./routes/exercises/audio-match/audio-match').then(
        (m) => m.AudioMatch,
      ),
  },
  {
    path: 'audio-memory/:id',
    loadComponent: () =>
      import('./routes/exercises/audio-memory/audio-memory').then(
        (m) => m.AudioMemory,
      ),
  },
  {
    path: 'word-audio-match/:id',
    loadComponent: () =>
      import('./routes/exercises/word-audio-match/word-audio-match').then(
        (m) => m.WordAudioMatch,
      ),
  },
  {
    path: 'comic/:id',
    loadComponent: () =>
      import('./routes/exercises/comic/comic').then((m) => m.Comic),
  },
  {
    path: 'phoneme-finder/:id',
    loadComponent: () =>
      import('./routes/exercises/phoneme-finder/phoneme-finder').then(
        (m) => m.PhonemeFinder,
      ),
  },
  {
    path: 'minimal-pair/:id',
    loadComponent: () =>
      import('./routes/exercises/minimal-pair/minimal-pair').then(
        (m) => m.MinimalPair,
      ),
  },
  {
    path: 'phoneme-level/:id',
    loadComponent: () =>
      import('./routes/exercises/phoneme-level/phoneme-level').then(
        (m) => m.PhonemeLevel,
      ),
  },
  {
    path: 'shape-recognition/:id',
    loadComponent: () =>
      import('./routes/shape-recognition/shape-recognition').then(
        (m) => m.ShapeRecognition,
      ),
  },

  {
    path: 'shape-size/:id',
    loadComponent: () =>
      import('./routes/shape-size/shape-size').then((m) => m.ShapeSize),
  },
  {
    path: 'path-game/:id',
    loadComponent: () =>
      import('./routes/path-game/path-game').then((m) => m.PathGame),
  },
  {
    path: 'patients/:id',
    loadComponent: () =>
      import('./routes/patients/patient-stats/patient-stats').then(
        (m) => m.PatientStats,
      ),
    canActivate: [practiceEmployeeGuard],
  },
  {
    path: 'statistics',
    loadComponent: () =>
      import('./routes/statistics/statistics').then((m) => m.Statistics),
  },
  {
    path: 'achievements',
    loadComponent: () =>
      import('./routes/achievements/achievements').then((m) => m.Achievements),
  },
  {
    path: 'sentence-attempts',
    loadComponent: () =>
      import('./routes/settings/sentence-attempts-admin/sentence-attempts-admin').then(
        (m) => m.SentenceAttemptsAdmin,
      ),
    canActivate: [adminGuard],
  },
  {
    path: 'withdrawals',
    loadComponent: () =>
      import('./routes/withdrawals-admin/withdrawals-admin').then(
        (m) => m.WithdrawalsAdmin,
      ),
    canActivate: [adminGuard],
  },
  {
    path: 'license-dashboard',
    loadComponent: () =>
      import('./routes/license-dashboard/license-dashboard').then(
        (m) => m.LicenseDashboard,
      ),
    canActivate: [adminGuard],
  },
  {
    path: 'practice-details',
    loadComponent: () =>
      import('./routes/practice-administration/practice-details/practice-details').then(
        (m) => m.PracticeDetailsAdmin,
      ),
    canActivate: [practiceEmployeeGuard],
  },
  {
    path: 'patients',
    loadComponent: () =>
      import('./routes/practice-administration/patients/patients').then(
        (m) => m.PatientsAdmin,
      ),
    canActivate: [practiceEmployeeGuard],
  },
  {
    path: 'employees',
    loadComponent: () =>
      import('./routes/practice-administration/employees/employees').then(
        (m) => m.Employees,
      ),
    canActivate: [practiceAdminGuard],
  },
  {
    path: 'licenses',
    loadComponent: () =>
      import('./routes/practice-administration/licenses/licenses').then(
        (m) => m.Licenses,
      ),
    canActivate: [practiceEmployeeGuard],
  },
  {
    path: 'billing',
    loadComponent: () =>
      import('./routes/practice-administration/billing/billing').then(
        (m) => m.Billing,
      ),
    canActivate: [practiceEmployeeGuard],
  },
  {
    path: 'practice-administration',
    loadComponent: () =>
      import('./routes/practice-administration/practice-administration').then(
        (m) => m.PracticeAdministration,
      ),
    canActivate: [practiceEmployeeGuard],
    children: [
      {
        path: '',
        redirectTo: 'practice-details',
        pathMatch: 'full',
      },
      {
        path: 'practice-details',
        loadComponent: () =>
          import('./routes/practice-administration/practice-details/practice-details').then(
            (m) => m.PracticeDetailsAdmin,
          ),
      },
      {
        path: 'patients',
        loadComponent: () =>
          import('./routes/practice-administration/patients/patients').then(
            (m) => m.PatientsAdmin,
          ),
      },
      {
        path: 'employees',
        loadComponent: () =>
          import('./routes/practice-administration/employees/employees').then(
            (m) => m.Employees,
          ),
      },
      {
        path: 'licenses',
        loadComponent: () =>
          import('./routes/practice-administration/licenses/licenses').then(
            (m) => m.Licenses,
          ),
      },
      {
        path: 'billing',
        loadComponent: () =>
          import('./routes/practice-administration/billing/billing').then(
            (m) => m.Billing,
          ),
      },
    ],
  },
  {
    path: 'settings',
    loadComponent: () =>
      import('./routes/settings/settings').then((m) => m.Settings),
    canActivate: [adminGuard],
    children: [
      {
        path: '',
        redirectTo: 'sentences',
        pathMatch: 'full',
      },
      {
        path: 'achievements',
        loadComponent: () =>
          import('./routes/settings/achievement-administration/achievement-administration').then(
            (m) => m.AchievementAdministration,
          ),
      },
      {
        path: 'levels',
        loadComponent: () =>
          import('./routes/settings/level-administration/level-administration').then(
            (m) => m.LevelAdministration,
          ),
      },
      {
        path: 'phonemes',
        loadComponent: () =>
          import('./routes/settings/phoneme-administration/phoneme-administration').then(
            (m) => m.PhonemeAdministration,
          ),
      },
      {
        path: 'target-sounds',
        loadComponent: () =>
          import('./routes/settings/target-sound-administration/target-sound-administration').then(
            (m) => m.TargetSoundAdministration,
          ),
      },
      {
        path: 'sentences',
        loadComponent: () =>
          import('./routes/settings/sentence-administration/sentence-administration').then(
            (m) => m.SentenceAdministration,
          ),
      },
      {
        path: 'users',
        loadComponent: () =>
          import('./routes/settings/user-administration/user-administration').then(
            (m) => m.UserAdministration,
          ),
      },
      {
        path: 'coupons',
        loadComponent: () =>
          import('./routes/settings/coupon-administration/coupon-administration').then(
            (m) => m.CouponAdministration,
          ),
      },
      {
        path: 'game-definitions',
        loadComponent: () =>
          import('./routes/settings/game-administration/game-administration').then(
            (m) => m.GameAdministration,
          ),
      },
      {
        path: 'goals',
        loadComponent: () =>
          import('./routes/settings/goal-administration/goal-administration').then(
            (m) => m.GoalAdministration,
          ),
      },
      {
        path: 'practices',
        loadComponent: () =>
          import('./routes/settings/practice-administration/practice-administration').then(
            (m) => m.PracticeAdministration,
          ),
      },
      {
        path: 'practices/:id',
        loadComponent: () =>
          import('./routes/settings/practice-administration/practice-details/practice-details').then(
            (m) => m.PracticeDetails,
          ),
      },
    ],
  },
];

export const routes: Routes = [
  {
    path: 'signup',
    component: Signup,
    canActivate: [authRedirectGuard],
  },
  {
    path: 'signin',
    component: Signin,
    canActivate: [authRedirectGuard],
  },
  {
    path: 'verify-email',
    loadComponent: () =>
      import('./routes/verify-email/verify-email').then((m) => m.VerifyEmail),
  },
  {
    path: 'token',
    component: TokenComponent,
  },
  {
    path: 'app/token',
    component: TokenComponent,
  },
  {
    path: 'agb',
    loadComponent: () => import('./routes/agb/agb').then((m) => m.Agb),
  },
  {
    path: 'privacy-policy',
    loadComponent: () =>
      import('./routes/privacy-policy/privacy-policy').then(
        (m) => m.PrivacyPolicy,
      ),
  },
  {
    path: 'delete-account',
    loadComponent: () =>
      import('./routes/delete-account/delete-account').then(
        (m) => m.DeleteAccount,
      ),
  },
  {
    path: '',
    component: ShellLayoutComponent,
    canActivate: [AuthGuard],
    children: [...MAIN_APP_ROUTES],
  },
  {
    path: 'app',
    component: ShellLayoutComponent,
    canActivate: [AuthGuard],
    children: MAIN_APP_ROUTES,
  },
  {
    path: '**',
    redirectTo: '',
  },
];
